import React, { useEffect } from 'react';
import { styled } from '@this/constants/themes';
import ContentBody from '@this/components/shared/atoms/content_body';

import { AirRoutePolicies } from '@this/components/reserve_trip/reserve_confirm/air_route_policies';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import type ReserveConfirmStore from '@this/src/domain/reserve_confirm_store';
import type Service from '@this/domain/service';

import SimpleLoading from '../../shared/simple_loading/simple_loading';
import ReserveConfirmOutlineTemplate from './outline/outline.template';
import ReserveConfirmRoutingsTemplate from './routings/routings.template';

import StepBar from '../../step_bar';
import { steps } from './reserve_confirm';

export type Props = {
  service: Service;
  store: ReserveConfirmStore | null;
  loading: boolean;
  responseError?: string;
  returning: boolean;
  itemsBlankError: boolean;
  showError: boolean;
  handleBackToSelectClick: () => void;
  handlePaymentSubmit: () => void;
};

const ReserveConfirmPcTemplate: React.FC<Props> = ({
  service,
  store,
  loading,
  responseError,
  returning,
  itemsBlankError,
  showError,
  handleBackToSelectClick,
  handlePaymentSubmit
}) => {
  const history = useHistory();
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    itemsBlankError &&
      setTimeout(() => {
        history.push('/dashboard');
      }, 5 * 1000);
  }, [history, itemsBlankError]);

  return (
    <Confirm style={loading ? { height: '100vh' } : undefined}>
      <ContentWrapper>
        <Body>
          {loading ? (
            <SimpleLoading />
          ) : responseError ? (
            responseError ===
            '検索条件の紐付けに失敗いたしました。おそれいりますが、下記のリンクを確認の上、再度ご予約をおねがいいたします。' ? (
              <ReturningError>
                検索条件の紐付けに失敗いたしました。
                <br />
                おそれいりますが、下記のリンクを確認の上、再度ご予約をおねがいいたします。
                <br />
                <br />
                <KnowledgeLink
                  href="https://aitravel.jp/knowledges/000001106"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  エラーメッセージが表示されて、交通機関・ホテルの予約ができない場合はどうすればいいですか？
                </KnowledgeLink>
              </ReturningError>
            ) : (
              <ReturningError>{responseError}</ReturningError>
            )
          ) : returning ? (
            <>
              <ReturningError>
                この手配依頼はすでに受け付けられている可能性があります。
                <br />
                <a href="/trips">こちら</a>
                から依頼済みの旅程をご確認ください。
                <br />
                該当するご旅程が確認できない場合は、もう一度ご依頼ください。
              </ReturningError>
              <BackTop>
                <a href="/">トップへ戻る</a>
              </BackTop>
            </>
          ) : itemsBlankError ? (
            <>
              <ReturningError>
                検索条件に該当する商品がありません。検索条件を変更して再度検索してください。
                <br />
                5秒後に検索画面へ遷移します。
              </ReturningError>
              <BackTop>
                <a href="/">トップへ戻る</a>
              </BackTop>
            </>
          ) : !store?.reserveInfo || !store?.reserveInfo.reservingTrip ? (
            <ShowError>
              通信環境が不安定です。
              <br />
              時間をおいてもう一度お試しください。
            </ShowError>
          ) : showError ? (
            <ShowError>
              通信環境が不安定です。
              <br />
              時間をおいてもう一度お試しください。
            </ShowError>
          ) : (
            <>
              <ConfirmWrapHeader>
                <StepBar steps={steps} index={store.stepIndex} />
              </ConfirmWrapHeader>
              <ConfirmWrap>
                <ConfirmWrapContent>
                  <ReserveConfirmRoutingsTemplate
                    store={store}
                    handleBackToSelectClick={handleBackToSelectClick}
                    handlePaymentSubmit={handlePaymentSubmit}
                  />
                </ConfirmWrapContent>
                <RightNav>
                  <Card>
                    <ReserveConfirmOutlineTemplate service={service} reserveInfo={store.reserveInfo} />
                  </Card>
                  {store.reserveInfo.reservingTrip.airRouteDetail && (
                    <Card>
                      <AirRoutePolicies airRoutePolicies={store.reserveInfo.reservingTrip.airRouteDetail} />
                    </Card>
                  )}
                </RightNav>
              </ConfirmWrap>
            </>
          )}
        </Body>
      </ContentWrapper>
    </Confirm>
  );
};

const baseMargin = '20px';
const pcWidth = '1150px';
const pcBaseMargin = '20px';
const rightNaviWidth = '400px';

const Confirm = styled.div`
  min-height: 100vh;
`;

const ReturningError = styled.div`
  padding: ${baseMargin};
  color: ${props => props.theme.redColor};
`;

const KnowledgeLink = styled.a`
  color: ${props => props.theme.accentColor};
  text-decoration: underline;
  &:hover {
    color: ${props => props.theme.accentColorHover};
  }
`;

const ShowError = styled.div`
  padding: ${baseMargin};
  color: ${props => props.theme.redColor};
`;

const BackTop = styled.div`
  margin-top: ${baseMargin};
  text-align: center;
  text-decoration: underline;
`;

const ContentWrapper = styled.div`
  flex-grow: 9999;
  display: flex;
  flex-direction: column;
  min-height: 0%;
  width: 100%;
  background: ${props => props.theme.wrapperBgColor};
`;

const Body = styled(ContentBody)`
  max-width: ${pcWidth};
  padding: ${pcBaseMargin};
  background: none;
`;

export const ConfirmWrapHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const ConfirmWrap = styled.div`
  display: flex;
`;

const ConfirmWrapContent = styled.div`
  width: calc(100% - ${rightNaviWidth} - 20px);
  background-color: white;
  padding: 10px 20px;
`;

const RightNav = styled.div`
  width: ${rightNaviWidth};
  margin-left: 20px;
`;

const Card = styled.div`
  padding: 10px 20px;
  font-size: 13px;
  min-height: 300px;
  background-color: white;

  & + & {
    margin-top: 20px;
  }
`;

export default observer(ReserveConfirmPcTemplate);
